import { useCallback } from 'react';
import { matchPath, useHistory, useLocation } from 'react-router';
import { useLocalStorage } from 'usehooks-ts';
import get from 'lodash/get';
import { routePaths } from '@css-xp/ppc-constants';
import type { PartnerProfile } from '@css-xp/ppc-types';
import { getRouteUrl } from '@css-xp/ppc-utils';

export const usePartnerRedirectHandler = (userProfile: PartnerProfile | null) => {
  const history = useHistory();
  const location = useLocation();
  const [, setSelectedPartnerAccount] = useLocalStorage(`${userProfile?.atlassianAccountId}-selectedTxa`, '');

  const handleRedirectionForPPC = useCallback(() => {
    const partnerAccounts = get(userProfile, 'partnerAccounts', []);

    const urlTxaId = Object.values(routePaths).reduce((acc, routePath) => {
      const matchPathRes = matchPath(location.pathname, {
        path: routePath,
        exact: true,
        strict: false,
      });
      return get(matchPathRes, 'params.txaId', acc);
    }, null);

    if (partnerAccounts.length === 0) {
      setSelectedPartnerAccount('');
      history.push(routePaths.unauthorized);
    }
    // Navigate to locally stored or 1st transaction account, if transaction account is not specified
    else if (!urlTxaId) {
      // Fetch locally stored data real time
      const locallyStoredTxa = window.localStorage.getItem(`${userProfile?.atlassianAccountId}-selectedTxa`);
      const parsedLocalTxa = locallyStoredTxa ? JSON.parse(locallyStoredTxa) : '';

      const transactionAccountToSelect =
        partnerAccounts.find(
          ({ transactionAccountId }: { transactionAccountId: string }) => parsedLocalTxa === transactionAccountId
        )?.transactionAccountId || partnerAccounts[0].transactionAccountId;
      setSelectedPartnerAccount(transactionAccountToSelect);
      // If Path is not specified, then navigate to home route
      if (location.pathname === '/') {
        history.push(
          getRouteUrl(routePaths.home, {
            txaId: transactionAccountToSelect,
          })
        );
      }
    }
    // Set transaction account on local storage, if transaction account is specified in URL
    else if (
      urlTxaId &&
      partnerAccounts.find(
        ({ transactionAccountId }: { transactionAccountId: string }) => urlTxaId === transactionAccountId
      )
    ) {
      setSelectedPartnerAccount(urlTxaId);
    }
    // Navigate to error screen if none of the case matches
    else {
      setSelectedPartnerAccount('');
      history.push('/error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);
  return [handleRedirectionForPPC];
};
